import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Background, Column, Page, Section, Widget } from "@emkit/builder-core";
import { buildColumnStyle, buildSectionStyle } from "@emkit/builder-runtime";
import { createBuiltinRegistry } from "@emkit/builder-widgets";
import type { CSSProperties, MouseEvent } from "react";
import { useMemo } from "react";
import {
  useEditor,
  useSelectedColumnId,
  useSelectedSectionId,
  useSelectedWidgetId,
} from "../store.js";

const registry = createBuiltinRegistry();

const BP_FRAME_WIDTH: Record<string, number | undefined> = {
  desktop: undefined,
  tablet: 768,
  mobile: 390,
};

export function Canvas() {
  const page = useEditor((s) => s.page);
  const clearSelection = useEditor((s) => s.clearSelection);
  const addSection = useEditor((s) => s.addSection);
  const mode = useEditor((s) => s.mode);
  const breakpoint = useEditor((s) => s.breakpoint);

  const onClickBackdrop = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) clearSelection();
  };

  const frameWidth = BP_FRAME_WIDTH[breakpoint];
  const innerStyle: CSSProperties = frameWidth
    ? {
        maxWidth: frameWidth,
        width: frameWidth,
        margin: "24px auto",
        boxShadow: "0 1px 2px rgba(0,0,0,0.06), 0 12px 36px rgba(0,0,0,0.12)",
      }
    : {};

  return (
    <main
      className={`emke-canvas emke-canvas--${breakpoint}${mode === "preview" ? " emke-canvas--preview" : ""}`}
      onClick={onClickBackdrop}
    >
      <div className="emke-canvas-inner" onClick={onClickBackdrop} style={innerStyle}>
        {page.sections.map((section) => (
          <SectionRenderer
            key={section.id}
            section={section}
            interactive={mode === "edit"}
            breakpoint={breakpoint}
          />
        ))}
        {mode === "edit" && (
          <button
            type="button"
            className="emke-add-section"
            onClick={(e) => {
              e.stopPropagation();
              addSection();
            }}
          >
            + Add section
          </button>
        )}
      </div>
    </main>
  );
}

interface SectionRendererProps {
  section: Section;
  interactive: boolean;
  breakpoint: "desktop" | "tablet" | "mobile";
}

function SectionRenderer({ section, interactive, breakpoint }: SectionRendererProps) {
  const selectSection = useEditor((s) => s.selectSection);
  const selectedSectionId = useSelectedSectionId();
  const isSelected = selectedSectionId === section.id;

  const styles = useMemo(
    () => buildSectionStyle(section, breakpoint),
    [section, breakpoint],
  );

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    if (!interactive) return;
    e.stopPropagation();
    selectSection(section.id);
  };

  const outerStyle: CSSProperties = {
    ...(styles.outer as CSSProperties),
    outline: isSelected ? "2px solid var(--accent)" : undefined,
    outlineOffset: isSelected ? "-2px" : undefined,
  };

  return (
    <section
      className={`emk-section${isSelected ? " is-selected" : ""}`}
      data-section-id={section.id}
      style={outerStyle}
      onClick={handleClick}
    >
      {styles.hasVideoBackground && <SectionVideoLayer bg={section.background} />}
      <div className="emk-section-inner" style={styles.inner as CSSProperties}>
        {section.columns.map((column) => (
          <ColumnRenderer
            key={column.id}
            column={column}
            interactive={interactive}
            breakpoint={breakpoint}
          />
        ))}
      </div>
    </section>
  );
}

function SectionVideoLayer({ bg }: { bg: Background | undefined }) {
  if (!bg || bg.kind !== "video") return null;
  return (
    <video
      className="emk-section-bg-video"
      autoPlay
      muted
      loop
      playsInline
      poster={bg.poster}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
        zIndex: 0,
        pointerEvents: "none",
      }}
    >
      <source src={bg.url} />
    </video>
  );
}

interface ColumnRendererProps {
  column: Column;
  interactive: boolean;
  breakpoint: "desktop" | "tablet" | "mobile";
}

function ColumnRenderer({ column, interactive, breakpoint }: ColumnRendererProps) {
  const selectColumn = useEditor((s) => s.selectColumn);
  const selectedColumnId = useSelectedColumnId();
  const isSelected = selectedColumnId === column.id;

  const style = useMemo(
    () => buildColumnStyle(column, breakpoint) as CSSProperties,
    [column, breakpoint],
  );

  const { setNodeRef, isOver } = useDroppable({
    id: `col:${column.id}`,
    data: { kind: "column", columnId: column.id },
    disabled: !interactive,
  });

  // Stable reference so SortableContext isn't forced to rebuild its
  // internal index on every parent render.
  const ids = useMemo(() => column.widgets.map((w) => w.id), [column.widgets]);

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (!interactive) return;
    e.stopPropagation();
    selectColumn(column.id);
  };

  const combinedStyle: CSSProperties = {
    ...style,
    minHeight: column.widgets.length === 0 ? 48 : undefined,
    outline: isOver
      ? "1px dashed var(--accent, #a855f7)"
      : isSelected
        ? "1px solid var(--accent)"
        : undefined,
    outlineOffset: isOver || isSelected ? "-1px" : undefined,
    borderRadius: isOver ? 6 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      className={`emk-column${isSelected ? " is-selected" : ""}`}
      data-column-id={column.id}
      style={combinedStyle}
      onClick={handleClick}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {column.widgets.map((widget) => (
          <SortableWidget
            key={widget.id}
            widget={widget}
            columnId={column.id}
            interactive={interactive}
            breakpoint={breakpoint}
          />
        ))}
      </SortableContext>
      {column.widgets.length === 0 && interactive ? (
        <div
          style={{
            color: "#9ca3af",
            fontSize: 12,
            textAlign: "center",
            padding: "8px 4px",
            pointerEvents: "none",
          }}
        >
          Drop a widget here
        </div>
      ) : null}
    </div>
  );
}

interface SortableWidgetProps {
  widget: Widget;
  columnId: string;
  interactive: boolean;
  breakpoint: "desktop" | "tablet" | "mobile";
}

function SortableWidget({
  widget,
  columnId,
  interactive,
  breakpoint,
}: SortableWidgetProps) {
  const selectedWidgetId = useSelectedWidgetId();
  const isSelected = selectedWidgetId === widget.id;
  const selectWidget = useEditor((s) => s.selectWidget);

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: widget.id,
    data: { kind: "widget", widgetId: widget.id, columnId },
    disabled: !interactive,
  });

  const html = useMemo(
    () => registry.render(widget, { breakpoint }),
    [widget, breakpoint],
  );

  const handleWrapperClick = (e: MouseEvent<HTMLDivElement>) => {
    if (!interactive) return;
    e.stopPropagation();
    selectWidget(widget.id);
  };

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      className={`emk-widget${isSelected ? " is-selected" : ""}`}
      data-widget-id={widget.id}
      data-widget-type={widget.type}
      onClick={handleWrapperClick}
      style={style}
    >
      {interactive && (
        <button
          ref={setActivatorNodeRef}
          type="button"
          className="emk-drag-handle"
          aria-label="Drag to reorder"
          title="Drag to reorder"
          onClick={(e) => e.stopPropagation()}
          {...attributes}
          {...listeners}
        >
          <span aria-hidden="true">⋮⋮</span>
        </button>
      )}
      <div
        className="emk-widget-body"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: widget HTML is produced in-process by our trusted registry from our typed Widget state.
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

export type { Page };
